/*
 * Copyright (c) 2025-present TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 */

import { getUrlParam, observeElementAdded, onDocumentReady } from '@theme/utilities';

/**
 * Create the signed-data div for Verified Buyer authentication
 */
function createSignedData() {
  // get data
  const signature = getUrlParam('yotpo_signature');
  const timestamp = getUrlParam('yotpo_timestamp');
  const name = getUrlParam('yotpo_name');
  const email = getUrlParam('yotpo_email');
  if (!signature || !timestamp || !name || !email) {
    return;
  }

  // remove existing element
  document.querySelector('.yotpo-signed-data')?.remove();

  // create signed data element
  const div = document.createElement('div');
  div.className = 'yotpo-signed-data';
  div.dataset.userName = name;
  div.dataset.userEmail = email;
  div.dataset.signature = signature;
  div.dataset.reviewerType = 'verified_buyer';
  div.dataset.timeStamp = timestamp;
  document.body.appendChild(div);
}

/**
 * Fix double ":" in German translation
 */
observeElementAdded('.yotpo-dropdown-placeholder', function (element) {
  if (element.textContent.includes('::')) {
    element.textContent = element.textContent.replace('::', ':');
  }
});

/**
 * Move the review star rating to the left panel
 */
observeElementAdded('.yotpo-review-star-rating', function (element) {
  // element has already been moved to the correct position
  if (!element.closest('.yotpo-review-left-panel')) {
    const reviewContainer = element.closest('.yotpo-review');
    if (reviewContainer) {
      const leftPanel = reviewContainer.querySelector('.yotpo-review-left-panel');
      leftPanel.insertBefore(element, leftPanel.firstChild);
    }
  }
});

/**
 * Move review votes to the center panel
 */
observeElementAdded('.yotpo-review-votes-wrapper', function (element) {
  const previous = element.previousElementSibling;
  if (previous && previous.classList.contains('yotpo-review')) {
    const targetContainer = previous.querySelector('.yotpo-review-center-panel');
    if (targetContainer) {
      targetContainer.appendChild(element);
    }
  }
});

/**
 * Change review date format
 */
observeElementAdded('.yotpo-date-format', function (element) {
  // parse date assuming format "DD/MM/YY" or "DD/MM/YYYY"
  const parts = element.textContent.trim().match(/^(\d{2})\/(\d{2})\/(\d{2,4})$/);
  if (!parts) {
    return;
  }

  const [, day, month, year] = parts;
  const fullYear = year.length === 2 ? '20' + year : year;

  const date = new Date(`${fullYear}-${month}-${day}`);
  // noinspection JSCheckFunctionSignatures
  if (isNaN(date)) {
    return;
  }

  // set the new format
  element.textContent = date.toLocaleDateString(navigator.language || 'de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
});

/**
 * Hide the reviews tab when no reviews are present
 */
observeElementAdded('.yotpo-empty-state', function (element) {
  element.closest('.yotpo-widget-instance').style.display = 'none';
});

/*
 * On load
 */
onDocumentReady(() => {
  // create signed data from url params
  createSignedData();

  /*
   * yotpo only assigns the click event to the first button with the class "yotpo-rewards-pop-up"
   * @see https://support.yotpo.com/docs/my-activity-on-site-module
   */
  const popupOpeners = document.querySelectorAll('.yotpo-rewards-pop-up');
  popupOpeners.forEach((popupOpener) => {
    popupOpener.addEventListener('click', () => {
      if (popupOpener !== popupOpeners[0]) {
        popupOpeners[0].dispatchEvent(new Event('click'));
      }
    });
  });

  /*
   * Open the review form and fill
   *
   * @example /products/fjaellraeven-kanken-rucksack-23510-118/?yotpo_action=add_review&yotpo_name=Test&yotpo_email=test@example.com
   */
  if (getUrlParam('yotpo_action') === 'add_review') {
    observeElementAdded('.yotpo-new-review-btn', function (element) {
      setTimeout(() => {
        // open form
        element.click();

        // pre-fill input fields
        setTimeout(() => {
          const name = getUrlParam('yotpo_name');
          const nameField = document.querySelector('.yotpo-review-form input[name="name"]');
          if (nameField && name) {
            nameField.value = name;
            nameField.dispatchEvent(new Event('input'));
          }
          const email = getUrlParam('yotpo_email');
          const emailField = document.querySelector('.yotpo-review-form input[name="email"]');
          if (emailField && email) {
            emailField.value = email;
            emailField.dispatchEvent(new Event('input'));
          }
        }, 300);
      }, 2000);
    });
  }
});
